import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    CollectionInterface,
    CollectionPayloadInterface,
} from "../../common/interfaces/collections/CollectionInterface";
import { collectionProductMapper } from "../../common/utils/collectionProductMapper";

interface CollectionsPageInterface {
    collections: CollectionInterface[];
}

const initialState: CollectionsPageInterface = {
    collections: [],
};

const collectionsSlice = createSlice({
    name: "collections",
    initialState,
    reducers: {
        mapCollection: (
            state,
            action: PayloadAction<CollectionPayloadInterface[] | undefined>,
        ) => {
            if (action.payload) {
                state.collections = [];

                action.payload.forEach(
                    (payloadCollection: CollectionPayloadInterface) => {
                        const numberOfProducts = 10;

                        const collection: CollectionInterface = {
                            id: payloadCollection.id,
                            name: payloadCollection.name,
                            productIds: payloadCollection.products.map(
                                (product) => product.id,
                            ),
                            machineryCount: 0,
                            accessoriesCount: 0,
                            sparePartsCount: 0,
                            machinery: [],
                            accessories: [],
                            spareParts: [],
                        };

                        const machinery = payloadCollection.products.filter(
                            (product) =>
                                product.productFamily?.code !== "accessories" &&
                                product.productFamily?.code !== "spare_parts",
                        );
                        const accessories = payloadCollection.products.filter(
                            (product) =>
                                product.productFamily?.code === "accessories",
                        );
                        const spareParts = payloadCollection.products.filter(
                            (product) =>
                                product.productFamily?.code === "spare_parts",
                        );

                        collection.machineryCount = machinery.length;
                        collection.accessoriesCount = accessories.length;
                        collection.sparePartsCount = spareParts.length;

                        collection.machinery = collectionProductMapper(
                            machinery,
                            numberOfProducts,
                        );
                        collection.accessories = collectionProductMapper(
                            accessories,
                            numberOfProducts,
                        );
                        collection.spareParts = collectionProductMapper(
                            spareParts,
                            numberOfProducts,
                        );

                        state.collections = [...state.collections, collection];
                    },
                );
            }
        },
        addCollectionProductIds: (
            state,
            action: PayloadAction<{ collectionId: number; productId: number }>,
        ) => {
            state.collections = state.collections.map((collection) => {
                if (collection.id === action.payload.collectionId) {
                    collection.productIds = [
                        ...collection.productIds,
                        action.payload.productId,
                    ];
                }
                return collection;
            });
        },
        removeCollectionProductId: (
            state,
            action: PayloadAction<{ collectionId: number; productId: number }>,
        ) => {
            state.collections = state.collections.map((collection) => {
                if (collection.id === action.payload.collectionId) {
                    collection.productIds = collection.productIds.filter(
                        (productId) => productId !== action.payload.productId,
                    );
                }
                return collection;
            });
        },
    },
});

export const {
    mapCollection,
    addCollectionProductIds,
    removeCollectionProductId,
} = collectionsSlice.actions;
export const collectionsReducer = collectionsSlice.reducer;

export {
    useGetCollectionsQuery,
    useLazyGetCollectionsQuery,
    useDeleteCollectionMutation,
    useUpdateCollectionMutation,
    useCreateCollectionMutation,
} from "../../common/api/collections";
